import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const RBACContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Permission constants matching backend
export const PERMISSIONS = {
  CAN_VIEW_MINUTES: "can_view_minutes",
  CAN_EDIT_MINUTES: "can_edit_minutes",
  CAN_APPROVE_MINUTES: "can_approve_minutes",
  CAN_SIGN_MINUTES: "can_sign_minutes",
  CAN_LOCK_MINUTES: "can_lock_minutes",
  CAN_VIEW_TRANSCRIPT: "can_view_transcript",
  CAN_VIEW_AUDIO: "can_view_audio",
  CAN_DOWNLOAD_AUDIO: "can_download_audio",
  CAN_EXPORT_MINUTES: "can_export_minutes",
  CAN_CREATE_RESOLUTIONS: "can_create_resolutions",
  CAN_VIEW_EVIDENCE: "can_view_evidence",
  CAN_MANAGE_LEGAL_HOLDS: "can_manage_legal_holds",
  CAN_VIEW_AUDIT_LOG: "can_view_audit_log",
  CAN_VIEW_DECLARATIONS: "can_view_declarations",
};

// Governance roles
export const GOVERNANCE_ROLES = {
  CHAIR: "chair",
  SECRETARY: "secretary",
  LEGAL: "legal",
  DIRECTOR: "director",
  OBSERVER: "observer",
};

// Default permissions (full access for non-RBAC entities)
const DEFAULT_PERMISSIONS = Object.values(PERMISSIONS).reduce((acc, perm) => {
  acc[perm] = true;
  return acc;
}, {});

export const RBACProvider = ({ children }) => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
  const [userRole, setUserRole] = useState(null);
  const [rbacEnabled, setRbacEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentMeetingId, setCurrentMeetingId] = useState(null);
  const [roleAssignments, setRoleAssignments] = useState([]);

  // Fetch user's permissions for a specific meeting context
  const fetchPermissions = useCallback(async (meetingId = null) => {
    if (!user) {
      setPermissions(DEFAULT_PERMISSIONS);
      setUserRole(null);
      setRbacEnabled(false);
      setLoading(false);
      return;
    }

    try {
      const endpoint = meetingId 
        ? `${API}/rbac/my-permissions?meeting_id=${meetingId}`
        : `${API}/rbac/my-permissions`;
      
      const response = await axios.get(endpoint);
      const data = response.data;
      
      setPermissions(data.permissions || DEFAULT_PERMISSIONS);
      setUserRole(data.role || null);
      setRbacEnabled(data.rbac_enabled || false);
      setCurrentMeetingId(meetingId);
    } catch (error) {
      // If RBAC endpoint fails, fall back to full permissions
      console.log("RBAC fetch failed, using default permissions");
      setPermissions(DEFAULT_PERMISSIONS);
      setRbacEnabled(false);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch role assignments for admin management
  const fetchRoleAssignments = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(`${API}/rbac/roles`);
      setRoleAssignments(response.data.role_assignments || []);
    } catch (error) {
      console.log("Failed to fetch role assignments");
    }
  }, [user]);

  // Update permissions when user changes
  useEffect(() => {
    if (user) {
      fetchPermissions();
      fetchRoleAssignments();
    } else {
      setPermissions(DEFAULT_PERMISSIONS);
      setUserRole(null);
      setRbacEnabled(false);
      setLoading(false);
    }
  }, [user, fetchPermissions, fetchRoleAssignments]);

  // Check if user has a specific permission
  const hasPermission = useCallback((permission) => {
    // Admin always has all permissions
    if (user?.role === "company_admin" || user?.role === "admin") {
      return true;
    }
    return permissions[permission] === true;
  }, [permissions, user]);

  // Check multiple permissions (returns true if ALL are granted)
  const hasAllPermissions = useCallback((permissionList) => {
    return permissionList.every(perm => hasPermission(perm));
  }, [hasPermission]);

  // Check multiple permissions (returns true if ANY is granted)
  const hasAnyPermission = useCallback((permissionList) => {
    return permissionList.some(perm => hasPermission(perm));
  }, [hasPermission]);

  // Check if user has a specific role
  const hasRole = useCallback((role) => {
    if (!rbacEnabled) return true;
    return userRole === role;
  }, [userRole, rbacEnabled]);

  // Check if user has any of the specified roles
  const hasAnyRole = useCallback((roles) => {
    if (!rbacEnabled) return true;
    return roles.includes(userRole);
  }, [userRole, rbacEnabled]);

  // Assign a role to a user
  const assignRole = async (userId, governanceRole, meetingScope = null, committeeScope = null) => {
    try {
      await axios.post(`${API}/rbac/assign`, {
        user_id: userId,
        governance_role: governanceRole,
        meeting_scope: meetingScope,
        committee_scope: committeeScope,
      });
      await fetchRoleAssignments();
      return true;
    } catch (error) {
      console.error("Failed to assign role:", error);
      throw error;
    }
  };

  // Remove a role assignment
  const removeRole = async (assignmentId) => {
    try {
      await axios.delete(`${API}/rbac/roles/${assignmentId}`);
      await fetchRoleAssignments();
      return true;
    } catch (error) {
      console.error("Failed to remove role:", error);
      throw error;
    }
  };

  // Set meeting context for permissions
  const setMeetingContext = useCallback((meetingId) => {
    if (meetingId !== currentMeetingId) {
      fetchPermissions(meetingId);
    }
  }, [currentMeetingId, fetchPermissions]);

  // Clear meeting context
  const clearMeetingContext = useCallback(() => {
    if (currentMeetingId) {
      fetchPermissions(null);
    }
  }, [currentMeetingId, fetchPermissions]);

  const value = {
    permissions,
    userRole,
    rbacEnabled,
    loading,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    hasRole,
    hasAnyRole,
    setMeetingContext,
    clearMeetingContext,
    assignRole,
    removeRole,
    roleAssignments,
    fetchRoleAssignments,
    refreshPermissions: fetchPermissions,
    PERMISSIONS,
    GOVERNANCE_ROLES,
  };

  return (
    <RBACContext.Provider value={value}>
      {children}
    </RBACContext.Provider>
  );
};

export const useRBAC = () => {
  const context = useContext(RBACContext);
  if (!context) {
    throw new Error("useRBAC must be used within an RBACProvider");
  }
  return context;
};

// Higher-order component for permission-based rendering
export const withPermission = (WrappedComponent, requiredPermission) => {
  return function PermissionWrapper(props) {
    const { hasPermission, loading } = useRBAC();
    
    if (loading) return null;
    if (!hasPermission(requiredPermission)) return null;
    
    return <WrappedComponent {...props} />;
  };
};

export default RBACContext;
